import axios from "axios";
import { useEffect, useState } from "react";
import {
  Link,
  Typography,
  Button,
  Card,
  CardContent,
  Box,
  Alert,
} from "@mui/material";
import { BackForeColor } from "../../../components/common/BackForeColor";
import {
  GetDateInDDMMYYYYFormat,
  GetDateInYYYYMMDDWithoutTimeFormat,
  OperationalStatusEnum,
  generateNoDataValues,
  getOperationalStatusNameFromId,
  getOperationalStatusStyleName,
} from "../../../common/utils";
import useGlobalContext from "../../../hooks/useGlobalContext";
import ActionButtons from "../../../components/common/ActionButtons";
import ValueOrNoDataLabel from "../../../components/common/ValueOrNoDataLabel";
import ModalBackground from "../../../components/common/ModalBackground";
import EventSearch from "../../../components/common/EventSearch";
import localize from "../../../common/localize";
import TableComponent from "../../../components/common/TableComponent";
import TotalValueItem from "../../event/components/TotalValueItem";
import { getClearEventPaymentModel } from "../../../common/EntitiesManagement";
import PaymentReport from "../../event/components/PaymentReport";
import ScrollablePageTab from "../../dynamicForm/components/ScrollablePageTab";
import { TabContext, TabPanel } from "@mui/lab";
import { useNavigate, useParams } from "react-router-dom";
import LoadingPanel from "../../../components/common/LoadingPanel";
import { Add } from "@mui/icons-material";
import NoMatch from "../../../components/NoMatch";

export default function BillEdition({ billData, setBillData, setShow }) {
  const { config, BILL_ENDPOINT, EVENTS_API } = useGlobalContext();
  const [eventsList, setEventsList] = useState([]);
  const [relatedToBillEventsList, setRelatedToBillEventsList] = useState([]);
  const [searchList, setSearchList] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [processResult, setProcessResult] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showEventPayment, setShowEventPayment] = useState(false);
  const [showSaveConfirmationPayment, setShowSaveConfirmationPayment] =
    useState(false);
  const [selectedTab, setSelectedtab] = useState("0");
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();
  const [billModel, setBillModel] = useState(billData);
  const [showNoDataPage, setShowNoDataPage] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      getBillData();
    }
  }, [id]);

  useEffect(() => {
    setBillData && setBillData(billModel);
  }, [billModel]);

  const getBillData = async () => {
    const bill = await axios.get(`${BILL_ENDPOINT}/${id}`, config);

    if ((bill.data.Ok || bill.data.ok) && bill.data?.Data) {
      setBillModel({
        ...bill.data.Data,
      });
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setShowNoDataPage(true);
  };

  useEffect(() => {
    if (billModel?.Id > 0) {
      getRelatedEvents(billModel?.Id);
      return;
    }

    setRelatedToBillEventsList([]);
  }, [billModel?.Id]);

  useEffect(() => {
    if (searchList?.length > 0) {
      setEventsList([...eventsList, ...searchList]);
    }
  }, [searchList]);

  const getRelatedEvents = async (billId) => {
    const relatedEvents = await axios.get(
      `${EVENTS_API}/bill/${billId}`,
      config
    );

    setRelatedToBillEventsList(relatedEvents?.data?.Data);
  };

  const CreateBillInformation = async () => {
    try {
      setProcessResult(false);
      if (billModel.Id > 0) {
        const putResult = await axios.put(
          `${BILL_ENDPOINT}/${billModel.Id}`,
          {
            ...billModel,
            GenerationDate: GetDateInYYYYMMDDWithoutTimeFormat(
              new Date(billModel?.GenerationDate)
            ),
            EventId:
              eventsList?.length > 0 ? eventsList.map((x) => x.Id) : null,
          },
          config
        );

        validateOperationResult(
          putResult.data,
          localize.success_bill_registration
        );
        return;
      }

      const postResult = await axios.post(
        `${BILL_ENDPOINT}/`,
        {
          ...billModel,
          GenerationDate: GetDateInYYYYMMDDWithoutTimeFormat(
            billModel.GenerationDate
          ),
          EventId: eventsList?.length > 0 ? eventsList.map((x) => x.Id) : null,
        },
        config
      );

      validateOperationResult(
        postResult.data,
        localize.success_bill_registration
      );
    } catch (error) {
      setSuccessMessage(`${localize.error_unexpected}: ${error.message}`);
      setShowSuccessMessage(true);
    }
  };

  const validateOperationResult = (result, successMessage) => {
    try {
      if (!result.Ok) {
        const data = JSON.parse(result?.data || result?.Data);
        const codes = [];
        const newEventsList = [];

        for (let index = 0; index < data?.length; index += 1) {
          const eventIndex = eventsList.findIndex((x) => x.Id === data[index]);
          if (eventIndex >= 0) {
            newEventsList.push(eventsList[eventIndex]);
            codes.push(eventsList[eventIndex].Code);
          }
        }

        setEventsList([...newEventsList]);

        setSuccessMessage(
          `${localize.error_unexpected}: ${JSON.stringify(codes)}`
        );
        setShowSuccessMessage(true);
        return;
      }

      setProcessResult(true);

      setSuccessMessage(successMessage);
      setShowSuccessMessage(true);
    } catch (error) {
      setSuccessMessage(`${localize.error_unexpected}: ${error.message}`);
      setShowSuccessMessage(true);
    }
  };

  const RemoveEventFromList = (eventId) => {
    const index = eventsList.findIndex((x) => x.Id === eventId);
    if (index >= 0) {
      eventsList.splice(index, 1);
      setEventsList([...eventsList]);
    }
  };

  const generateEventsInformationTable = (eventsDataList) => (
    <TableComponent
      noPadding
      firstColumnTitle={localize.actions}
      columnTitles={[
        localize.code,
        localize.event_type,
        localize.operational_status,
        localize.execution_status,
        localize.proceeding,
        localize.client_name,
        localize.total_value,
      ]}
      results={
        eventsDataList && eventsDataList?.length > 0
          ? eventsDataList.map((item) => ({
              key: item.Id,
              data: [
                <ActionButtons
                  hideEditButton
                  key={`${item.Id}_action`}
                  onclickForDeleteButton={() => {
                    RemoveEventFromList(item.Id);
                  }}
                />,
                <Link
                  color="primary"
                  underline="always"
                  href="#"
                  onClick={() => {
                    window.open(`/events/detail/${item.Id}`, "_blank");
                  }}
                >{`${item.Code}`}</Link>,
                <BackForeColor
                  backColor={item.EventType.BackgroundColor}
                  foreColor={item.EventType.ForegroundColor}
                  text={item.EventType.Name}
                />,
                <p
                  style={{ borderRadius: "40px" }}
                  className={`rounded p-2 ${getOperationalStatusStyleName(
                    item.OperationalStatus
                  )}`}
                >
                  {getOperationalStatusNameFromId(item.OperationalStatus)}
                </p>,
                <BackForeColor
                  backColor={
                    item.OperationalStatus === OperationalStatusEnum.Canceled
                      ? "white"
                      : item.CurrentStatus?.BackgroundColor
                  }
                  foreColor={
                    item.OperationalStatus === OperationalStatusEnum.Canceled
                      ? "red"
                      : item.CurrentStatus?.ForegroundColor
                  }
                  text={
                    item.OperationalStatus === OperationalStatusEnum.Canceled
                      ? localize.cancelled
                      : item.CurrentStatus?.Name
                  }
                  styleName={
                    item.OperationalStatus === OperationalStatusEnum.Canceled
                      ? "cancelled_operational_status"
                      : ""
                  }
                  TooltipText={
                    item.OperationalStatus === OperationalStatusEnum.Canceled
                      ? item.FinishObservation
                      : undefined
                  }
                />,
                <ValueOrNoDataLabel data={item.ProceedingInfo?.Name} />,
                <ValueOrNoDataLabel data={item.Client?.Name} />,
                <TotalValueItem item={item} />,
              ],
            }))
          : [generateNoDataValues(8)]
      }
    />
  );

  const UpdatePaymentDate = async () => {
    try {
      if (relatedToBillEventsList?.length > 0) {
        const result = await axios.put(
          `${EVENTS_API}/payment`,
          {
            ...paymentInfo,
            PaymentDate: GetDateInYYYYMMDDWithoutTimeFormat(
              paymentInfo.PaymentDate
            ),
            EventIds: relatedToBillEventsList.map((x) => x.Id),
          },
          config
        );

        validateOperationResult(
          result.data,
          localize.success_payment_registration_message
        );
      }
    } catch (error) {
      setSuccessMessage(`${localize.error_unexpected}: ${error.message}`);
      setShowSuccessMessage(true);
    }
  };

  const getTabPages = () => {
    const result = [
      {
        Id: "0",
        Name: localize.orders_to_relate_to_bill,
      },
    ];

    if (relatedToBillEventsList?.length > 0) {
      result.push({
        Id: "1",
        Name: `${localize.orders_related_to_bill} (${
          relatedToBillEventsList?.length ?? 0
        })`,
      });
    }

    return result;
  };

  const changeTab = (value) => {
    value >= 0 && setSelectedtab(value.toString());
  };

  const showUI = () => {
    if (showNoDataPage) {
      return <NoMatch />;
    }

    if (isLoading) {
      return <LoadingPanel />;
    }

    return !billModel ? (
      <></>
    ) : (
      <div>
        {showSaveConfirmationPayment && (
          <ModalBackground
            showModal={showSaveConfirmationPayment}
            setShowModal={setShowSaveConfirmationPayment}
            title={localize.finish_register_payment}
            okButtonText={localize.continue}
            onClickToCancel={() => {
              setShowSaveConfirmationPayment(false);
            }}
            onClickToSubmit={() => {
              setShowSaveConfirmationPayment(false);
              UpdatePaymentDate();
            }}
            Component={
              <Typography>{localize.save_payment_confirmation}</Typography>
            }
          />
        )}

        {showEventPayment && (
          <ModalBackground
            showModal={showEventPayment}
            setShowModal={setShowEventPayment}
            title={localize.register_payment}
            okButtonText={localize.accept}
            onClickToCancel={() => {
              setPaymentInfo(getClearEventPaymentModel());
              setShowEventPayment(false);
            }}
            onClickToSubmit={() => {
              setShowEventPayment(false);
              setShowSaveConfirmationPayment(true);
            }}
            validators={
              (paymentInfo?.Payed && !paymentInfo.PaymentMethodId) ||
              paymentInfo?.PaymentMethodId <= 0 ||
              (!paymentInfo?.Payed &&
                paymentInfo?.NoPaymentObservation?.length <= 0)
            }
            Component={
              <PaymentReport
                paymentModel={paymentInfo}
                setPaymentModel={setPaymentInfo}
              />
            }
          />
        )}

        {showSuccessMessage && (
          <ModalBackground
            showModal={showSuccessMessage}
            setShowModal={setShowSuccessMessage}
            title={localize.save_changes}
            okButtonText={localize.accept}
            hideCancelButton
            onClickToSubmit={() => {
              setShowSuccessMessage(false);

              if (processResult) {
                setBillModel(null);
                setBillData(null);
                setEventsList([]);
                setShow(false);
                navigate("/bills");
                // window.location.reload(false);
              }
            }}
            Component={<Typography>{successMessage}</Typography>}
          />
        )}

        {showSearch && (
          <EventSearch
            showSearch={showSearch}
            setShowSearch={setShowSearch}
            setResultList={setSearchList}
          />
        )}

        {showSaveConfirmation && (
          <ModalBackground
            showModal={showSaveConfirmation}
            setShowModal={setShowSaveConfirmation}
            title={localize.save_changes}
            okButtonText={localize.continue}
            onClickToCancel={() => {
              setShowSaveConfirmation(false);
            }}
            onClickToSubmit={() => {
              CreateBillInformation();
              setShowSaveConfirmation(false);
            }}
            Component={
              <Typography>{localize.save_billing_confirmation}</Typography>
            }
          />
        )}

        {showDeleteConfirmation && (
          <ModalBackground
            showModal={showDeleteConfirmation}
            setShowModal={setShowDeleteConfirmation}
            title={localize.delete_bill_information}
            okButtonText={localize.accept}
            cancelButtonText={localize.continue_editing}
            onClickToCancel={() => {
              setShowDeleteConfirmation(false);
            }}
            onClickToSubmit={() => {
              setShowDeleteConfirmation(false);
              setShow && setShow();
              navigate("/bills");
            }}
            Component={
              <Typography>{localize.delete_bill_info_confirmation}</Typography>
            }
          />
        )}

        <div className="flex flex-col">
          <Card
            variant="outlined"
            style={{
              margin: "16px",
              boxShadow:
                "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
            }}
          >
            <CardContent>
              <Box className="w-full flex gap-2 justify-between">
                <Box className="two_columns_grid">
                  {billModel?.Id > 0 && (
                    <Typography>
                      <b>{`${localize.bill}:`}</b>
                    </Typography>
                  )}
                  {billModel?.Id > 0 && (
                    <Typography>{billModel?.Id}</Typography>
                  )}
                  <Typography>
                    <b>{`${localize.bill_code}:`}</b>
                  </Typography>
                  <Typography>{billModel?.Code}</Typography>
                  <Typography>
                    <b>{`${localize.bill_date}:`}</b>
                  </Typography>
                  <Typography>
                    {GetDateInDDMMYYYYFormat(
                      new Date(billModel?.GenerationDate)
                    )}
                  </Typography>
                  {billModel?.Client && (
                    <Typography>
                      <b>{`${localize.client}:`}</b>
                    </Typography>
                  )}
                  {billModel?.Client && (
                    <Typography>{`${billModel.Client.Identification} - ${billModel.Client.Name}`}</Typography>
                  )}
                  <Typography>
                    <b>{`${localize.modification_date}:`}</b>
                  </Typography>
                  <Typography>
                    {new Date(billModel?.ModificationDate).toLocaleString(
                      undefined,
                      {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      }
                    )}
                  </Typography>
                  <Typography>
                    <b>{`${localize.modified_by}:`}</b>
                  </Typography>
                  <Typography>{billModel.CreatedBy}</Typography>

                  {billModel?.Observation?.length > 0 && (
                    <Typography>
                      <b>{`${localize.observations}:`}</b>
                    </Typography>
                  )}
                  {billModel?.Observation?.length > 0 && (
                    <Typography>{billModel.Observation}</Typography>
                  )}
                </Box>
                <Box className="flex flex-col gap-2">
                  <Button
                    sx={{ height: "30px" }}
                    variant="contained"
                    color="success"
                    disabled={
                      (!billModel?.Id || billModel?.Id === 0) &&
                      (!eventsList || eventsList?.length === 0)
                    }
                    onClick={() => {
                      setShowSaveConfirmation(true);
                    }}
                  >
                    {localize.save_changes}
                  </Button>

                  <Button
                    sx={{ height: "30px" }}
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setShowDeleteConfirmation(true);
                    }}
                  >
                    {localize.cancel}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <TabContext value={selectedTab}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <ScrollablePageTab
                selectedPage={parseInt(selectedTab)}
                setSelectedPage={changeTab}
                noMarginTop
                dynamicFormPages={getTabPages()}
              />
            </Box>

            <TabPanel value="0">
              <div className="flex flex-col gap-4">
                <div className="flex items-bottom gap-4 items-center">
                  <Alert className="w-full my-2" severity="info">
                    <label className="text-xs">
                      {localize.add_events_to_bill}
                    </label>
                  </Alert>

                  <div
                    className="flex"
                    style={{
                      gap: "10px",
                      alignItems: "end",
                    }}
                  >
                    <Button
                      sx={{ height: "30px" }}
                      variant="contained"
                      color="success"
                      onClick={() => {
                        setSearchList([]);
                        setShowSearch(true);
                      }}
                    >
                      {localize.add}
                      <Add className="text-xs" />
                    </Button>
                  </div>
                </div>

                {generateEventsInformationTable(eventsList)}
              </div>
            </TabPanel>

            {relatedToBillEventsList?.length > 0 && (
              <TabPanel value="1">
                <div className="flex flex-col gap-4">
                  <div className="flex items-bottom gap-4 items-center">
                    <Alert className="w-full my-2" severity="info">
                      <label className="text-xs">
                        {localize.added_events_to_bill}
                      </label>
                    </Alert>

                    <div
                      className="flex"
                      style={{
                        gap: "10px",
                        alignItems: "end",
                      }}
                    >
                      <Button
                        sx={{
                          backgroundColor: "#10366E",
                          height: "30px",
                          width: "200px",
                        }}
                        variant="contained"
                        color="info"
                        disabled={
                          !relatedToBillEventsList ||
                          relatedToBillEventsList?.length <= 0
                        }
                        onClick={() => {
                          setPaymentInfo(getClearEventPaymentModel());
                          setShowEventPayment(true);
                        }}
                      >
                        {localize.register_payment}
                      </Button>
                    </div>
                  </div>

                  {generateEventsInformationTable(relatedToBillEventsList)}
                </div>
              </TabPanel>
            )}
          </TabContext>
        </div>
      </div>
    );
  };

  return showUI();
}
